import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { message, Modal, Input, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import { AiInboxSummaryBanner, FullSummaryModal } from './components/summary';
import InboxWorkspaceTabs from './components/InboxWorkspaceTabs';
import InboxControlsBar from './components/InboxControlsBar';
import InboxTimelineGroup from './components/InboxTimelineGroup';
import { TakeTourButton, useProjectTour } from '../tour';
import {
    getAiSummary,
    refreshAiSummary,
    getInboxFeed,
    updateItemReadState,
    updateItemBookmark,
    updateItemArchive,
    executeItemAction,
    markAllItemsAsRead,
} from './api/inboxApi';

/**
 * ProjectManagement Inbox Submodule
 * Centralized Action & Activity Control Center of the Project Management Module.
 * Implements Spec §1, §2, §3, §17, §18, §19, §20, §21, §22.
 */
export default function ProjectInbox({ onOpenTaskDrawer, onNavigate, onSelectProject, workspaceTasks = [] }) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    // ── State Management ───────────────────────────────────────────────────
    const [summaryData, setSummaryData] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('Past Week');
    const [customRange, setCustomRange] = useState({ start: null, end: null });
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [isFullSummaryOpen, setIsFullSummaryOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('all');
    const [inboxItems, setInboxItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [serverCounts, setServerCounts] = useState(null);
    const [serverProjects, setServerProjects] = useState([]);
    const [isFeedLoading, setIsFeedLoading] = useState(false);

    // Controls bar state
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [selectedProject, setSelectedProject] = useState('All');
    const [selectedPriority, setSelectedPriority] = useState('All');
    const [selectedSort, setSelectedSort] = useState('newest');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Pagination & Mentions Scope State
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [hasMore, setHasMore] = useState(false);
    const [totalFeedCount, setTotalFeedCount] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [mentionsScope, setMentionsScope] = useState('my');

    // ── Tour Guide Tab Synchronizer ─────────────────────────────────────────
    const { isOpen: isTourOpen, activeTourId, currentStep, currentTourDefinition } = useProjectTour();
    const prevTabBeforeTourRef = useRef(null);

    useEffect(() => {
        if (!isTourOpen || activeTourId !== 'inbox') {
            if (prevTabBeforeTourRef.current !== null) {
                setActiveTab(prevTabBeforeTourRef.current);
                prevTabBeforeTourRef.current = null;
            }
            return;
        }

        if (prevTabBeforeTourRef.current === null) {
            prevTabBeforeTourRef.current = activeTab;
        }

        const step = currentTourDefinition?.steps?.[currentStep];
        if (!step) return;

        if (step.selector === '[data-tour="inbox-tab-all"]') {
            setActiveTab('all');
        } else if (step.selector === '[data-tour="inbox-tab-action"]') {
            setActiveTab('action');
        } else if (step.selector === '[data-tour="inbox-tab-activity"]') {
            setActiveTab('activity');
        } else if (step.selector === '[data-tour="inbox-tab-mentions"]') {
            setActiveTab('mentions');
        } else if (step.selector === '[data-tour="inbox-tab-bookmarks"]') {
            setActiveTab('bookmarks');
        } else if (step.selector === '[data-tour="inbox-tab-archive"]') {
            setActiveTab('archive');
        }
    }, [isTourOpen, activeTourId, currentStep, currentTourDefinition]);

    // ── Unique Projects for Filter Dropdown ────────────────────────────────
    const projectOptions = useMemo(() => {
        const set = new Set(serverProjects);
        inboxItems.forEach((item) => {
            if (item.context?.project) set.add(item.context.project);
        });
        return Array.from(set);
    }, [inboxItems, serverProjects]);

    // ── Dynamic Tab Counts (Server-accurate with optimistic fallback) ─────
    const tabCounts = useMemo(() => {
        if (serverCounts) return serverCounts;
        const activeRecords = inboxItems.filter((i) => !i.archived);
        return {
            all: activeRecords.length,
            activity: activeRecords.filter((i) => i.category === 'activity').length,
            action: activeRecords.filter((i) => i.category === 'action').length,
            mentions: activeRecords.filter((i) => i.category === 'mentions').length,
            bookmarks: inboxItems.filter((i) => i.bookmarked).length,
            archive: inboxItems.filter((i) => i.archived).length,
            unread: activeRecords.filter((i) => !i.read).length,
        };
    }, [inboxItems, serverCounts]);

    // ── Per-Tab & Filter Memory Cache for 0ms Instant Switching ───────────
    const feedCacheRef = useRef({});
    const serverCountsRef = useRef(null);

    const getCacheKey = (tab, project, priority, search, sort, scope) =>
        `${tab}|${project}|${priority}|${(search || '').trim()}|${sort}|${scope}`;

    const syncAcrossCache = useCallback((updater) => {
        Object.keys(feedCacheRef.current).forEach((key) => {
            const entry = feedCacheRef.current[key];
            if (entry && Array.isArray(entry.items)) {
                entry.items = updater(entry.items);
            }
        });
    }, []);

    // ── Live Feed API Lifecycle ───────────────────────────────────────────
    const fetchFeed = useCallback(async (tab, project, priority, search, sort, currentPage = 1, scope = 'my', append = false, forceRefresh = false) => {
        const cacheKey = getCacheKey(tab, project, priority, search, sort, scope);

        if (!append && currentPage === 1 && !forceRefresh) {
            // 1. Instant 0ms cache hit if this tab/filter combination was already loaded or prefetched
            if (feedCacheRef.current[cacheKey]) {
                const cached = feedCacheRef.current[cacheKey];
                setInboxItems(cached.items);
                setHasMore(cached.hasMore);
                setTotalFeedCount(cached.total);
                setPage(cached.page || 1);
                return;
            }

            // 2. Instant 0ms empty resolution if server counts already report 0 items for this tab
            if (
                scope === 'my' &&
                project === 'All' &&
                priority === 'All' &&
                !(search || '').trim() &&
                serverCountsRef.current &&
                serverCountsRef.current[tab] === 0
            ) {
                feedCacheRef.current[cacheKey] = { items: [], hasMore: false, total: 0, page: 1 };
                setInboxItems([]);
                setHasMore(false);
                setTotalFeedCount(0);
                setPage(1);
                return;
            }
        }

        try {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsFeedLoading(true);
            }
            const res = await getInboxFeed({
                tab,
                project,
                priority,
                search,
                sort,
                page: currentPage,
                limit: pageSize,
                mentionsScope: scope,
            });
            if (res?.success && res?.data) {
                const incoming = Array.isArray(res.data.items) ? res.data.items : [];
                if (append) {
                    setInboxItems((prev) => {
                        const existingIds = new Set(prev.map((i) => i.id));
                        const brandNew = incoming.filter((i) => !existingIds.has(i.id));
                        const merged = [...prev, ...brandNew];
                        feedCacheRef.current[cacheKey] = {
                            items: merged,
                            hasMore: Boolean(res.data.hasMore),
                            total: res.data.total || merged.length,
                            page: currentPage,
                        };
                        return merged;
                    });
                } else {
                    setInboxItems(incoming);
                    feedCacheRef.current[cacheKey] = {
                        items: incoming,
                        hasMore: Boolean(res.data.hasMore),
                        total: res.data.total || incoming.length,
                        page: 1,
                    };
                }
                setHasMore(Boolean(res.data.hasMore));
                setTotalFeedCount(res.data.total || incoming.length);
                if (res.data.counts) {
                    serverCountsRef.current = res.data.counts;
                    setServerCounts(res.data.counts);

                    // Silently pre-warm non-empty sibling tabs in the background so tab clicks are 0ms
                    if (!append && currentPage === 1) {
                        const counts = res.data.counts;
                        ['all', 'activity', 'action', 'mentions', 'bookmarks', 'archive']
                            .filter((t) => t !== tab)
                            .forEach((siblingTab) => {
                                const siblingKey = getCacheKey(siblingTab, project, priority, search, sort, scope);
                                if (feedCacheRef.current[siblingKey]) return;
                                if (counts[siblingTab] === 0 && project === 'All' && priority === 'All' && !(search || '').trim()) {
                                    feedCacheRef.current[siblingKey] = { items: [], hasMore: false, total: 0, page: 1 };
                                } else if (counts[siblingTab] > 0) {
                                    getInboxFeed({
                                        tab: siblingTab,
                                        project,
                                        priority,
                                        search,
                                        sort,
                                        page: 1,
                                        limit: pageSize,
                                        mentionsScope: scope,
                                    })
                                        .then((sRes) => {
                                            if (sRes?.success && sRes?.data && !feedCacheRef.current[siblingKey]) {
                                                const sItems = Array.isArray(sRes.data.items) ? sRes.data.items : [];
                                                feedCacheRef.current[siblingKey] = {
                                                    items: sItems,
                                                    hasMore: Boolean(sRes.data.hasMore),
                                                    total: sRes.data.total || sItems.length,
                                                    page: 1,
                                                };
                                            }
                                        })
                                        .catch(() => {});
                                }
                            });
                    }
                }
                if (Array.isArray(res.data.projects)) {
                    setServerProjects(res.data.projects);
                }
            }
        } catch (err) {
            console.warn('[Inbox] Live feed fetch failed, using active workspace state:', err.message);
        } finally {
            setIsFeedLoading(false);
            setIsLoadingMore(false);
        }
    }, [pageSize]);

    useEffect(() => {
        setPage(1);
        fetchFeed(activeTab, selectedProject, selectedPriority, debouncedSearchQuery, selectedSort, 1, mentionsScope, false);
    }, [activeTab, selectedProject, selectedPriority, debouncedSearchQuery, selectedSort, mentionsScope, fetchFeed]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchFeed(activeTab, selectedProject, selectedPriority, debouncedSearchQuery, selectedSort, nextPage, mentionsScope, true);
    };

    const handleRefreshFeed = async () => {
        feedCacheRef.current = {};
        setPage(1);
        await fetchFeed(activeTab, selectedProject, selectedPriority, debouncedSearchQuery, selectedSort, 1, mentionsScope, false, true);
        message.success({ content: 'Inbox data refreshed', key: 'inbox-feed-refresh', duration: 1.5 });
    };

    // Real-time socket event listener for instant Inbox sync
    useEffect(() => {
        if (!currentAdmin?._id) return;
        const socket = connectSocket({ userId: currentAdmin._id, role: currentAdmin.role });
        if (!socket) return;

        const handleRealtimeEvent = () => {
            feedCacheRef.current = {};
            fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false, true);
        };

        socket.on('inbox:new_event', handleRealtimeEvent);
        socket.on('inbox:updated_event', handleRealtimeEvent);
        socket.on('new_notification', handleRealtimeEvent);

        return () => {
            socket.off('inbox:new_event', handleRealtimeEvent);
            socket.off('inbox:updated_event', handleRealtimeEvent);
            socket.off('new_notification', handleRealtimeEvent);
            releaseSocket();
        };
    }, [currentAdmin?._id, currentAdmin?.role, activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, mentionsScope, fetchFeed]);

    // ── Item Actions: Read / Bookmark / Archive / Select ──────────────────
    const handleToggleRead = async (id) => {
        const target = inboxItems.find((i) => i.id === id);
        const nextRead = !target?.read;
        const updater = (list) => list.map((item) => (item.id === id ? { ...item, read: nextRead } : item));
        setInboxItems(updater);
        syncAcrossCache(updater);
        try {
            await updateItemReadState(id, nextRead);
        } catch (err) {
            console.warn('[Inbox] Failed to persist read state:', err.message);
        }
    };

    const handleToggleBookmark = async (id) => {
        const target = inboxItems.find((i) => i.id === id);
        const nextVal = !target?.bookmarked;
        const updater = (list) => list.map((item) => (item.id === id ? { ...item, bookmarked: nextVal } : item));
        setInboxItems(updater);
        syncAcrossCache(updater);
        // Invalidate cached bookmarks tab so switching to Bookmarks shows updated list
        Object.keys(feedCacheRef.current).forEach((k) => {
            if (k.startsWith('bookmarks|')) delete feedCacheRef.current[k];
        });
        setServerCounts((prev) => {
            if (!prev) return prev;
            const next = { ...prev, bookmarks: Math.max(0, (prev.bookmarks || 0) + (nextVal ? 1 : -1)) };
            serverCountsRef.current = next;
            return next;
        });
        message.success(nextVal ? 'Saved to Bookmarks' : 'Removed from Bookmarks');
        try {
            await updateItemBookmark(id, nextVal);
        } catch (err) {
            console.warn('[Inbox] Failed to persist bookmark state:', err.message);
        }
    };

    const handleToggleArchive = async (id) => {
        const target = inboxItems.find((i) => i.id === id);
        const nextVal = !target?.archived;
        setInboxItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, archived: nextVal } : item))
        );
        message.success(nextVal ? 'Item archived' : 'Item restored to active workspace');
        try {
            await updateItemArchive(id, nextVal);
            feedCacheRef.current = {};
            fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false, true);
        } catch (err) {
            console.warn('[Inbox] Failed to persist archive state:', err.message);
        }
    };

    const handleToggleSelect = (targetIdOrArray, forceState) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (Array.isArray(targetIdOrArray)) {
                targetIdOrArray.forEach((id) => {
                    if (forceState !== undefined) {
                        if (forceState) next.add(id);
                        else next.delete(id);
                    } else {
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                    }
                });
            } else {
                if (next.has(targetIdOrArray)) next.delete(targetIdOrArray);
                else next.add(targetIdOrArray);
            }
            return next;
        });
    };

    const handleMarkGroupAsRead = async (ids) => {
        const idSet = new Set(ids);
        const updater = (list) => list.map((item) => (idSet.has(item.id) ? { ...item, read: true } : item));
        setInboxItems(updater);
        syncAcrossCache(updater);
        message.success('Marked group as read');
        try {
            await markAllItemsAsRead(ids);
        } catch (err) {
            console.warn('[Inbox] Failed to persist group read state:', err.message);
        }
    };

    const handleMarkAllRead = async () => {
        const unreadIds = inboxItems.filter((i) => !i.read && !i.archived).map((i) => i.id);
        const updater = (list) => list.map((item) => ({ ...item, read: true }));
        setInboxItems(updater);
        syncAcrossCache(updater);
        message.success('All caught up! All items marked as read.');
        try {
            await markAllItemsAsRead(unreadIds);
        } catch (err) {
            console.warn('[Inbox] Failed to persist all read state:', err.message);
        }
    };

    // ── AI Summary API Lifecycle ─────────────────────────────────────────
    const fetchSummary = useCallback(async (period, customStart, customEnd, project) => {
        try {
            setIsSummaryLoading(true);
            const res = await getAiSummary({
                period: period?.startsWith('Custom:') ? 'Custom Range' : period,
                customStart,
                customEnd,
                project: project && project !== 'All' ? project : undefined,
            });
            if (res?.success && res?.data) {
                setSummaryData(res.data);
            }
        } catch (err) {
            console.warn('[Inbox] Failed to fetch AI summary, keeping current briefing:', err.message);
        } finally {
            setIsSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSummary(selectedPeriod, customRange.start, customRange.end, selectedProject);
    }, [selectedPeriod, customRange.start, customRange.end, selectedProject, fetchSummary]);

    const handleRefreshSummary = async () => {
        try {
            setIsSummaryLoading(true);
            message.loading({ content: 'Generating updated AI summary briefing...', key: 'ai-refresh' });
            const res = await refreshAiSummary({
                period: selectedPeriod?.startsWith('Custom:') ? 'Custom Range' : selectedPeriod,
                customStart: customRange.start,
                customEnd: customRange.end,
                project: selectedProject && selectedProject !== 'All' ? selectedProject : undefined,
            });
            if (res?.success && res?.data) {
                setSummaryData(res.data);
                message.success({ content: 'AI Inbox Summary refreshed!', key: 'ai-refresh' });
            } else {
                message.info({ content: 'AI Inbox Summary refreshed!', key: 'ai-refresh' });
            }
        } catch (err) {
            message.error({ content: 'Failed to refresh AI summary', key: 'ai-refresh' });
        } finally {
            setIsSummaryLoading(false);
        }
    };

    const handleCustomRangeSelect = (label, startStr, endStr) => {
        setSelectedPeriod(`Custom: ${label}`);
        setCustomRange({ start: startStr, end: endStr });
    };

    // Helper to guarantee a valid 24-character hexadecimal MongoDB ObjectId
    const resolveTaskId = (item, rawCandidate) => {
        // 1. Direct valid 24-hex candidate check
        const cleanCandidate = typeof rawCandidate === 'string'
            ? rawCandidate.replace(/^task-/, '').replace(/^notif-/, '')
            : rawCandidate;
        if (typeof cleanCandidate === 'string' && /^[0-9a-fA-F]{24}$/.test(cleanCandidate)) {
            return cleanCandidate;
        }
        if (item?.deepLink?.entityId && typeof item.deepLink.entityId === 'string' && /^[0-9a-fA-F]{24}$/.test(item.deepLink.entityId)) {
            return item.deepLink.entityId;
        }
        if (item?.taskId && typeof item.taskId === 'string' && /^[0-9a-fA-F]{24}$/.test(item.taskId)) {
            return item.taskId;
        }
        if (item?.sourceId && typeof item.sourceId === 'string' && /^[0-9a-fA-F]{24}$/.test(item.sourceId)) {
            return item.sourceId;
        }

        // 2. Intelligent workspace matching (by taskId code, title, or keywords from summary item)
        if (Array.isArray(workspaceTasks) && workspaceTasks.length > 0) {
            // First pass: exact taskId code match (e.g., TSK-2026-...)
            if (cleanCandidate) {
                const exactMatch = workspaceTasks.find((t) =>
                    (t.taskId && String(t.taskId).toLowerCase() === String(cleanCandidate).toLowerCase()) ||
                    (t._id && String(t._id) === String(cleanCandidate))
                );
                if (exactMatch?._id) return exactMatch._id;
            }

            // Second pass: match using summary item task title or search keys
            const candidatesToMatch = [
                item?.taskTitle,
                item?.context?.task,
                item?.title,
                cleanCandidate,
            ].filter(Boolean).map(s => String(s).toLowerCase().trim());

            for (const cand of candidatesToMatch) {
                if (!cand || cand.length < 3) continue;
                const matched = workspaceTasks.find((t) => {
                    const tTitle = (t.title || '').toLowerCase();
                    const tCode = (t.taskId || '').toLowerCase();
                    return (tTitle && (tTitle.includes(cand) || cand.includes(tTitle))) ||
                           (tCode && (tCode === cand || cand.includes(tCode)));
                });
                if (matched?._id) return matched._id;
            }

            // Third pass: keyword extraction from headline or text (e.g. "RWB Shopify Store")
            const fullText = (item?.headline || item?.text || item?.detailedStory || '').toLowerCase();
            if (fullText) {
                const bestMatch = workspaceTasks.find((t) => {
                    if (!t.title) return false;
                    const cleanTitle = t.title.toLowerCase().trim();
                    if (fullText.includes(cleanTitle)) return true;
                    // Check significant title tokens (>= 4 chars)
                    const words = cleanTitle.split(/\s+/).filter(w => w.length >= 4);
                    const matchCount = words.filter(w => fullText.includes(w)).length;
                    return words.length > 0 && matchCount >= Math.min(2, words.length);
                });
                if (bestMatch?._id) return bestMatch._id;
            }
        }
        return null;
    };

    // ── Deep-Link / Operational Action Handlers ───────────────────────────
    const handleActionClick = async (actionKey, item) => {
        switch (actionKey) {
            case 'approve':
                // Optimistic synchronous transition from Action Required -> Activity / Approved
                setInboxItems((prev) =>
                    prev.map((i) =>
                        i.id === item.id
                            ? {
                                  ...i,
                                  category: 'activity',
                                  type: 'APPROVED',
                                  read: true,
                                  actions: [{ key: 'open_task', label: 'View Details', icon: 'check', style: 'default' }],
                              }
                            : i
                    )
                );
                message.success(`Approved: ${item.context?.deliverable || item.title}`);
                try {
                    await executeItemAction(item.id, { action: 'approve' });
                    feedCacheRef.current = {};
                    fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false, true);
                } catch (err) {
                    console.warn('[Inbox] Action sync warning:', err.message);
                }
                break;

            case 'accept':
                setInboxItems((prev) =>
                    prev.map((i) =>
                        i.id === item.id
                            ? {
                                  ...i,
                                  category: 'activity',
                                  type: 'PROJECT ACCEPTED',
                                  read: true,
                                  actions: [{ key: 'open_task', label: 'View Details', icon: 'check', style: 'default' }],
                              }
                            : i
                    )
                );
                message.success(`Project scope accepted: ${item.context?.project}`);
                try {
                    await executeItemAction(item.id, { action: 'accept' });
                    feedCacheRef.current = {};
                    fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false, true);
                } catch (err) {
                    console.warn('[Inbox] Action sync warning:', err.message);
                }
                break;

            case 'resolve_blocker':
                setInboxItems((prev) =>
                    prev.map((i) =>
                        i.id === item.id
                            ? {
                                  ...i,
                                  category: 'activity',
                                  type: 'RESOLVED',
                                  read: true,
                                  actions: [{ key: 'open_task', label: 'View Details', icon: 'check', style: 'default' }],
                              }
                            : i
                    )
                );
                message.success(`Blocker resolved for: ${item.title}`);
                try {
                    await executeItemAction(item.id, { action: 'resolve_blocker' });
                    feedCacheRef.current = {};
                    fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false, true);
                } catch (err) {
                    console.warn('[Inbox] Action sync warning:', err.message);
                }
                break;

            case 'request_changes':
            case 'reject': {
                let changeNote = '';
                Modal.confirm({
                    title: 'Request Changes / Revision',
                    icon: <ExclamationCircleOutlined style={{ color: '#d97706' }} />,
                    content: (
                        <div className="mt-3 space-y-2">
                            <p className="text-xs text-slate-500">
                                Please provide specific revision feedback explaining what needs to be updated.
                            </p>
                            <Input.TextArea
                                id="revision-note-input"
                                rows={3}
                                placeholder="Detailed revision requirements (mandatory)..."
                                onChange={(e) => { changeNote = e.target.value; }}
                            />
                        </div>
                    ),
                    okText: 'Submit Request',
                    okButtonProps: { style: { background: '#e11d48', borderColor: '#e11d48', color: '#fff' } },
                    onOk: async () => {
                        if (!changeNote.trim()) {
                            message.error('A revision note is mandatory.');
                            return Promise.reject();
                        }
                        try {
                            await executeItemAction(item.id, { action: 'request_changes', note: changeNote.trim() });
                            message.success('Change request and revision notes registered.');
                            feedCacheRef.current = {};
                            fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false, true);
                        } catch (err) {
                            message.error('Failed to submit revision: ' + (err.response?.data?.message || err.message));
                        }
                    },
                });
                break;
            }

            case 'preview_file':
                if (onNavigate) {
                    onNavigate('files');
                } else {
                    message.info(`Opening file preview for ${item.title}`);
                }
                break;

            case 'view_project':
                if (onNavigate) {
                    onNavigate('dashboard');
                } else {
                    message.info(`Navigating to project dashboard`);
                }
                break;

            case 'view_report': {
                const isSprint =
                    item.deepLink?.target === 'sprint_board' ||
                    item.deepLink?.tab === 'sprint' ||
                    item.context?.sprint ||
                    (item.title && item.title.toLowerCase().includes('sprint'));

                if (isSprint) {
                    if (onNavigate) {
                        onNavigate('overview', { taskTab: 'sprint' });
                    } else {
                        message.info(`Navigating to Sprint Board`);
                    }
                } else if (onNavigate) {
                    onNavigate('milestones');
                } else {
                    message.info(`Navigating to milestones`);
                }
                break;
            }

            case 'adjust_sprint': {
                if (onNavigate) {
                    onNavigate('overview', { taskTab: 'sprint' });
                } else {
                    message.info(`Navigating to Sprint Board`);
                }
                break;
            }

            case 'open_milestone': {
                if (onNavigate) {
                    onNavigate('milestones');
                } else {
                    message.info(`Navigating to milestones`);
                }
                break;
            }

            case 'review_project_request':
            case 'view_project_request': {
                const reqId = item.deepLink?.requestId || item.deepLink?.entityId;
                const creId = item.deepLink?.creProjectId;
                const pcr = item.deepLink?.pcrNumber;
                const projName = item.deepLink?.projectName || item.context?.project || item.title;

                if (onNavigate) {
                    onNavigate('dashboard', {
                        openPendingRequests: true,
                        requestId: reqId,
                        creProjectId: creId,
                        pcrNumber: pcr,
                        projectName: projName,
                    });
                }
                break;
            }

            case 'view_blockers':
            case 'review':
            case 'open_task':
            case 'view_conversation':
            case 'reply': {
                // If it's a pending project review request, route to dashboard pending requests
                if (item.deepLink?.target === 'project_review_modal') {
                    if (onNavigate) {
                        onNavigate('dashboard', {
                            openPendingRequests: true,
                            requestId: item.deepLink?.requestId || item.deepLink?.entityId,
                            creProjectId: item.deepLink?.creProjectId,
                            pcrNumber: item.deepLink?.pcrNumber,
                            projectName: item.deepLink?.projectName || item.context?.project || item.title,
                        });
                        return;
                    }
                }
                // If it's explicitly a file deliverable, route to files
                if (item.deepLink?.target === 'file_preview' || item.deepLink?.nav === 'files') {
                    if (onNavigate) {
                        onNavigate('files');
                        return;
                    }
                }
                if (item.deepLink?.target === 'sprint_board' || item.deepLink?.tab === 'sprint') {
                    if (onNavigate) {
                        onNavigate('overview', { taskTab: 'sprint' });
                        return;
                    }
                }
                if (item.deepLink?.nav === 'dashboard') {
                    if (onNavigate) {
                        onNavigate('dashboard', {
                            openPendingRequests: item.deepLink?.target === 'project_review_modal',
                            requestId: item.deepLink?.requestId || item.deepLink?.entityId,
                            creProjectId: item.deepLink?.creProjectId,
                            pcrNumber: item.deepLink?.pcrNumber,
                            projectName: item.deepLink?.projectName || item.context?.project || item.title,
                        });
                        return;
                    }
                }
                if (item.deepLink?.nav === 'milestones') {
                    if (onNavigate) {
                        onNavigate('milestones');
                        return;
                    }
                }

                // Resolve task ID from entityId or strip task- prefix
                const entityId = item.deepLink?.entityId;
                const rawTaskId = entityId || (typeof item.id === 'string' && item.id.startsWith('task-') ? item.id.replace('task-', '') : (typeof item.id === 'string' && item.id.startsWith('notif-') ? null : item.id));

                const resolvedId = resolveTaskId(item, rawTaskId);

                if (resolvedId && onOpenTaskDrawer) {
                    onOpenTaskDrawer(resolvedId);
                } else if (onOpenTaskDrawer && typeof rawTaskId === 'string' && /^[0-9a-fA-F]{24}$/.test(rawTaskId)) {
                    onOpenTaskDrawer(rawTaskId);
                } else {
                    message.info(`Opening details for ${item.title}`);
                }
                break;
            }

            default:
                message.info(`Action triggered: ${actionKey}`);
                break;
        }
    };

    const handleSummaryItemClick = (summaryItem) => {
        const target = (summaryItem.actionTarget || '').toLowerCase();
        const nav = (summaryItem.targetNav || '').toLowerCase();
        const actionLabel = (summaryItem.actionLabel || '').toLowerCase();

        // Check if this action is task or comment related
        const isTaskOrComment =
            target === 'task_drawer' ||
            target.includes('task') ||
            target.includes('comment') ||
            target.includes('discussion') ||
            target.includes('mention') ||
            actionLabel.includes('task') ||
            actionLabel.includes('comment') ||
            actionLabel.includes('view') ||
            summaryItem.sourceType === 'task' ||
            summaryItem.sourceType === 'mention' ||
            summaryItem.entityType === 'task';

        if (isTaskOrComment) {
            const targetId = summaryItem.taskId || summaryItem.entityId || summaryItem.sourceId;
            const resolvedId = resolveTaskId(summaryItem, targetId);
            if (resolvedId && onOpenTaskDrawer) {
                message.info(`Opening task details for ${summaryItem.projectName || 'task'}`);
                onOpenTaskDrawer(resolvedId);
                return;
            }
        }

        // Project Dashboard navigation
        if (target === 'project_dashboard' || nav === 'dashboard' || actionLabel.includes('project')) {
            if (onNavigate) {
                onNavigate('dashboard');
                return;
            }
        }

        // Sprint Board navigation
        if (target === 'sprint_board' || (nav === 'overview' && summaryItem.targetTab === 'sprint')) {
            if (onNavigate) {
                onNavigate('overview', { taskTab: 'sprint' });
                return;
            }
        }

        // Milestones navigation
        if (target === 'milestone_details' || nav === 'milestones' || actionLabel.includes('milestone')) {
            if (onNavigate) {
                onNavigate('milestones');
                return;
            }
        }

        // Files navigation
        if (target === 'file_preview' || nav === 'files' || actionLabel.includes('file')) {
            if (onNavigate) {
                onNavigate('files');
                return;
            }
        }

        // Tabs navigation
        if (target === 'activity') {
            setActiveTab('activity');
            return;
        }
        if (target === 'action') {
            setActiveTab('action');
            return;
        }

        // Fallback: try resolving task ID directly
        if (onOpenTaskDrawer) {
            const fallbackId = resolveTaskId(summaryItem, summaryItem.entityId || summaryItem.sourceId);
            if (fallbackId) {
                onOpenTaskDrawer(fallbackId);
                return;
            }
        }

        message.info(`Navigating to ${summaryItem.projectName} (${summaryItem.actionLabel})`);
    };

    // ── Filter & Search Pipeline ──────────────────────────────────────────
    const filteredItems = useMemo(() => {
        return inboxItems.filter((item) => {
            // 1. Tab filter
            if (activeTab === 'bookmarks') {
                if (!item.bookmarked) return false;
            } else if (activeTab === 'archive') {
                if (!item.archived) return false;
            } else {
                if (item.archived) return false;
                if (activeTab === 'activity' && item.category !== 'activity') return false;
                if (activeTab === 'action' && item.category !== 'action') return false;
                if (activeTab === 'mentions' && item.category !== 'mentions') return false;
            }

            // 2. Project filter
            if (selectedProject !== 'All' && item.context?.project !== selectedProject) {
                return false;
            }

            // 3. Priority filter
            if (selectedPriority !== 'All') {
                const p = selectedPriority.toLowerCase();
                if (p === 'critical' && item.priorityLevel !== 'critical') return false;
                if (p === 'high priority' && item.priorityLevel !== 'high') return false;
                if (p === 'medium' && item.priorityLevel !== 'medium') return false;
                if (p === 'normal' && item.priorityLevel !== 'normal') return false;
            }

            // 4. Search query
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const titleMatch = item.title?.toLowerCase().includes(q);
                const descMatch = item.description?.toLowerCase().includes(q);
                const projMatch = item.context?.project?.toLowerCase().includes(q);
                const taskMatch = item.context?.task?.toLowerCase().includes(q);
                const actorMatch = item.actor?.name?.toLowerCase().includes(q);
                const quoteMatch = item.quote?.toLowerCase().includes(q);
                if (!titleMatch && !descMatch && !projMatch && !taskMatch && !actorMatch && !quoteMatch) {
                    return false;
                }
            }

            return true;
        });
    }, [inboxItems, activeTab, selectedProject, selectedPriority, searchQuery]);

    // ── Chronological Grouping ────────────────────────────────────────────
    const groupedItems = useMemo(() => {
        const groups = {
            today: [],
            yesterday: [],
            earlier: [],
            older: [],
        };

        filteredItems.forEach((item) => {
            if (groups[item.groupId]) {
                groups[item.groupId].push(item);
            } else {
                groups.older.push(item);
            }
        });

        return groups;
    }, [filteredItems]);

    // ── Active Filter Indicator Bar Status ────────────────────────────────
    const isFilterActive =
        selectedProject !== 'All' ||
        selectedPriority !== 'All' ||
        Boolean(searchQuery.trim()) ||
        activeTab !== 'all';

    const handleClearFilters = () => {
        setActiveTab('all');
        setSelectedProject('All');
        setSelectedPriority('All');
        setSearchQuery('');
    };

    const totalUnread = tabCounts.unread || 0;

    return (
        <div className="space-y-6 max-w-[1600px] mx-auto">
            {/* Header */}
            <div data-tour="inbox-header" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-1">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                            Inbox
                        </h1>
                        {totalUnread > 0 && (
                            <span className="bg-blue-100 dark:bg-blue-950/60 text-[#03488A] dark:text-[#00AAF1] text-xs font-bold px-2.5 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                                {totalUnread} unread
                            </span>
                        )}
                    </div>
                    <p className="text-[#424750] dark:text-slate-400 font-medium text-sm mt-0.5">
                        Enterprise Activity Feed &amp; Action Items
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {totalUnread > 0 && (
                        <button
                            data-tour="inbox-mark-all-read"
                            onClick={handleMarkAllRead}
                            className="h-8 px-3.5 text-xs font-bold leading-none text-slate-600 dark:text-slate-300 hover:text-[#03488A] dark:hover:text-[#00AAF1] bg-white dark:bg-[#1f1f1f] border border-slate-200 dark:border-[#303030] hover:border-slate-300 dark:hover:border-slate-600 rounded-xl shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-sm text-blue-600 dark:text-[#00AAF1]">done_all</span>
                            Mark all as read
                        </button>
                    )}
                    <TakeTourButton currentNav="inbox" onNavigate={onNavigate} />
                </div>
            </div>

            {/* Briefing & Controls Section */}
            <div className="space-y-5" id="briefing-and-controls-section">
                {/* 1. ✨ AI Inbox Summary (Top Briefing Layer) */}
                <div data-tour="inbox-ai-summary">
                    <AiInboxSummaryBanner
                        summaryData={summaryData}
                        selectedPeriod={selectedPeriod}
                        selectedProject={selectedProject}
                        onPeriodChange={setSelectedPeriod}
                        onCustomRangeSelect={handleCustomRangeSelect}
                        onRefresh={handleRefreshSummary}
                        onGenerate={handleRefreshSummary}
                        isLoading={isSummaryLoading}
                        onOpenFullModal={() => setIsFullSummaryOpen(true)}
                        onItemActionClick={handleSummaryItemClick}
                    />
                </div>

                {/* 2. Operational Workspace Tabs & Filter Toolbar */}
                <div className="bg-white dark:bg-[#181818] border border-slate-200/80 dark:border-[#303030] rounded-2xl shadow-2xs overflow-hidden transition-all">
                    {/* Workspace Tabs Navigation */}
                    <div data-tour="inbox-workspace-tabs">
                        <InboxWorkspaceTabs
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            counts={tabCounts}
                        />
                    </div>

                    {/* Controls Toolbar: Search, Filters, Sort */}
                    <div data-tour="inbox-controls-bar">
                        <InboxControlsBar
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            selectedProject={selectedProject}
                            onProjectChange={setSelectedProject}
                            projects={projectOptions}
                            selectedPriority={selectedPriority}
                            onPriorityChange={setSelectedPriority}
                            selectedSort={selectedSort}
                            onSortChange={setSelectedSort}
                            activeTab={activeTab}
                            isAdmin={['super_admin', 'admin'].includes((currentAdmin?.role || '').toLowerCase())}
                            mentionsScope={mentionsScope}
                            onMentionsScopeChange={setMentionsScope}
                            loading={isFeedLoading}
                            onRefresh={handleRefreshFeed}
                        />
                    </div>
                </div>
            </div>

            {/* 3. Chronological Activity Stream */}
            <div data-tour="inbox-timeline-stream" className="space-y-6 relative" id="activity-stream">
                <Spin spinning={isFeedLoading} tip="Loading updates...">
                    <div className="min-h-[200px]">
                        {filteredItems.length === 0 && !isFeedLoading ? (
                            <div className="bg-white dark:bg-[#1f1f1f] border border-slate-200/80 dark:border-[#303030] rounded-2xl p-12 text-center shadow-2xs">
                                <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600">inbox</span>
                                <h3 className="text-base font-bold text-slate-700 dark:text-slate-200 mt-3 font-headline">
                                    No items found
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                                    No activity or action required items match your current filter or search criteria.
                                </p>
                                {isFilterActive && (
                                    <button
                                        onClick={handleClearFilters}
                                        className="mt-4 h-8 px-3.5 inline-flex items-center justify-center text-xs font-bold leading-none text-[#03488A] dark:text-[#00AAF1] bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-all cursor-pointer"
                                    >
                                        Reset all filters
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <InboxTimelineGroup
                                    groupTitle="Today"
                                    items={groupedItems.today}
                                    selectedIds={selectedIds}
                                    onToggleSelect={handleToggleSelect}
                                    onToggleBookmark={handleToggleBookmark}
                                    onToggleArchive={handleToggleArchive}
                                    onToggleRead={handleToggleRead}
                                    onMarkGroupAsRead={handleMarkGroupAsRead}
                                    onActionClick={handleActionClick}
                                />

                                <InboxTimelineGroup
                                    groupTitle="Yesterday"
                                    items={groupedItems.yesterday}
                                    selectedIds={selectedIds}
                                    onToggleSelect={handleToggleSelect}
                                    onToggleBookmark={handleToggleBookmark}
                                    onToggleArchive={handleToggleArchive}
                                    onToggleRead={handleToggleRead}
                                    onMarkGroupAsRead={handleMarkGroupAsRead}
                                    onActionClick={handleActionClick}
                                />

                                <InboxTimelineGroup
                                    groupTitle="Earlier This Week"
                                    items={groupedItems.earlier}
                                    selectedIds={selectedIds}
                                    onToggleSelect={handleToggleSelect}
                                    onToggleBookmark={handleToggleBookmark}
                                    onToggleArchive={handleToggleArchive}
                                    onToggleRead={handleToggleRead}
                                    onMarkGroupAsRead={handleMarkGroupAsRead}
                                    onActionClick={handleActionClick}
                                />

                                {groupedItems.older.length > 0 && (
                                    <InboxTimelineGroup
                                        groupTitle="Older"
                                        items={groupedItems.older}
                                        selectedIds={selectedIds}
                                        onToggleSelect={handleToggleSelect}
                                        onToggleBookmark={handleToggleBookmark}
                                        onToggleArchive={handleToggleArchive}
                                        onToggleRead={handleToggleRead}
                                        onMarkGroupAsRead={handleMarkGroupAsRead}
                                        onActionClick={handleActionClick}
                                    />
                                )}

                                {/* Pagination / Load More Controls for High Volume */}
                                {hasMore && (
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-50/80 dark:bg-[#161616] border border-slate-200/60 dark:border-[#303030] rounded-2xl shadow-2xs mt-4 mb-6">
                                        <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                            Showing <span className="font-bold text-slate-700 dark:text-slate-200">{inboxItems.length}</span> of <span className="font-bold text-slate-700 dark:text-slate-200">{totalFeedCount}</span> updates
                                        </div>
                                        <button
                                            onClick={handleLoadMore}
                                            disabled={isLoadingMore}
                                            className="h-8 px-3.5 rounded-xl bg-white dark:bg-[#202020] hover:bg-slate-100 dark:hover:bg-[#282828] text-[#03488A] dark:text-[#00AAF1] border border-blue-200 dark:border-blue-900/60 text-xs font-bold leading-none inline-flex items-center gap-1.5 shadow-2xs hover:shadow-xs active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                                        >
                                            {isLoadingMore ? (
                                                <>
                                                    <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                                                    Loading More...
                                                </>
                                            ) : (
                                                <>
                                                    <span className="material-symbols-outlined text-sm">expand_more</span>
                                                    Load More Activity
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Spin>
            </div>

            {/* Expanded Full Summary Modal */}
            <FullSummaryModal
                visible={isFullSummaryOpen}
                onClose={() => setIsFullSummaryOpen(false)}
                summaryData={summaryData}
                selectedPeriod={selectedPeriod}
                selectedProject={selectedProject}
                onItemActionClick={handleSummaryItemClick}
            />
        </div>
    );
}
